import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Pulse, Plus, Trash, PencilSimple, FilePdf, Lock, Sparkle, ChartLine,
  Fire, CopySimple, TrendUp, FileText, ShieldCheck, Calendar, ArrowsClockwise,
} from "@phosphor-icons/react";
import {
  listSymptoms, createSymptom, updateSymptom, deleteSymptom, listPrescriptions,
  startEpisode, promoteSymptomToEpisode,
} from "@/lib/api";
import { toLocalDatetimeInputValue } from "@/lib/dateUtils";
import { createSymptomLogPdf } from "@/lib/symptomLogPdf";
import { createEvidenceSummaryPdf, createCpPrepPdf } from "@/lib/symptomEvidencePdf";
import { saveBytesAsPdf } from "@/lib/claimUtils";
import { showToast } from "@/lib/toast";
import SymptomCharts from "@/components/SymptomCharts";
import SymptomInsightsPanel from "@/components/SymptomInsightsPanel";
import SymptomEntryForm from "@/components/SymptomEntryForm";
import EpisodesPanel from "@/components/EpisodesPanel";
import { buildDuplicatePayload, TAG_OPTIONS } from "@/lib/symptomDiary";
import { VIEWS } from "@/lib/navConfig";

const FREE_LIMIT = 30;

function fmtDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-US", { dateStyle: "medium", timeStyle: iso.includes("T") ? "short" : undefined });
  } catch { return iso; }
}

function severityColor(sev) {
  if (sev >= 7) return "#C72E2E";
  if (sev >= 4) return "#D78D1A";
  if (sev >= 1) return "#1A8C52";
  return "#94A3B8";
}

export default function SymptomTrackerView({
  issues = [],
  workspace,
  profile,
  master526ez,
  isFree = false,
  setActiveView,
}) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formInitial, setFormInitial] = useState(null); // prefill for duplicate
  const [formMode, setFormMode] = useState("quick");
  const [filterIssue, setFilterIssue] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [filterTag, setFilterTag] = useState("");
  const [generating, setGenerating] = useState(false);
  const [pdfStatus, setPdfStatus] = useState("");
  const [prescriptions, setPrescriptions] = useState([]);
  const [reportScope, setReportScope] = useState("");
  const [reportBusy, setReportBusy] = useState(false);
  const [reportStatus, setReportStatus] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setItems(await listSymptoms());
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { listPrescriptions().then(setPrescriptions); }, []);

  const allTags = useMemo(() => {
    const set = new Set(TAG_OPTIONS);
    items.forEach((s) => (s.tags || []).forEach((t) => set.add(t)));
    return Array.from(set);
  }, [items]);

  const filtered = useMemo(() => {
    return items.filter((s) => {
      if (filterIssue === "__undx__") {
        if (!s.isUndiagnosed) return false;
      } else if (filterIssue && s.linkedIssueId !== filterIssue) {
        return false;
      }
      if (filterType !== "all" && s.entryType !== filterType) return false;
      if (filterTag && !(s.tags || []).includes(filterTag)) return false;
      return true;
    });
  }, [items, filterIssue, filterType, filterTag]);

  const aggregates = useMemo(() => {
    if (!items.length) return { count: 0, avg: 0, peak: 0, last: null };
    const severities = items.map((s) => Number(s.severity || 0));
    const dates = items.map((s) => s.date).sort();
    return {
      count: items.length,
      avg: severities.reduce((a, b) => a + b, 0) / severities.length,
      peak: Math.max(...severities),
      last: dates[dates.length - 1],
    };
  }, [items]);

  async function handleSave(payload) {
    if (editing?.id) {
      await updateSymptom(editing.id, payload);
    } else if (formMode === "episode") {
      // Start a new multi-day episode (creates the episode header + day-1 entry).
      await startEpisode(payload);
      window.dispatchEvent(new CustomEvent("claimops:episodes-refresh"));
      showToast({ variant: "success", message: "Ongoing episode started. Update it as it evolves.", duration: 3500 });
    } else {
      await createSymptom(payload);
    }
    setShowForm(false); setEditing(null); setFormInitial(null);
    await load();
  }

  async function handleDelete(item) {
    if (!window.confirm(`Delete symptom entry from ${fmtDate(item.date)}?`)) return;
    await deleteSymptom(item.id);
    await load();
    showToast({
      message: `Symptom entry from ${fmtDate(item.date)} deleted.`,
      duration: 6000,
      action: {
        label: "UNDO",
        onClick: async () => {
          try {
            const { id, user_id, ...payload } = item;
            void id; void user_id;
            await createSymptom(payload);
            await load();
            showToast({ variant: "success", message: "Symptom entry restored.", duration: 3000 });
          } catch (e) {
            showToast({ variant: "error", message: `Restore failed: ${e.message}` });
          }
        },
      },
    });
  }

  async function handleGeneratePdf() {
    setGenerating(true); setPdfStatus("");
    try {
      const bytes = await createSymptomLogPdf({
        workspace, profile, master526ez, issues, symptoms: items, isPro: !isFree,
      });
      const safeTitle = (workspace?.title || "VA-Claim").replace(/[^a-zA-Z0-9-_]+/g, "-");
      saveBytesAsPdf(bytes, `Symptom-Log-${safeTitle}${isFree ? "-FREE" : ""}.pdf`);
      setPdfStatus(isFree ? "Generated FREE symptom log (watermark). Upgrade to remove." : "Generated branded symptom log PDF.");
    } catch (e) {
      console.error("symptom PDF gen failed:", e);
      setPdfStatus(`Error: ${e.message}`);
    } finally {
      setGenerating(false);
    }
  }

  async function runReport(kind) {
    setReportBusy(true); setReportStatus("");
    try {
      const fn = kind === "evidence" ? createEvidenceSummaryPdf : createCpPrepPdf;
      // For the Evidence Summary, attach episode timelines so the PDF gets the
      // "Ongoing Episodes" section with multi-day VA-narratives.
      let episodeBundles = [];
      if (kind === "evidence") {
        const { listEpisodes, getEpisode } = await import("@/lib/api");
        const list = await listEpisodes();
        const filtered = reportScope
          ? list.filter((e) => e.linkedIssueId === reportScope)
          : list;
        episodeBundles = await Promise.all(
          filtered.map(async (e) => {
            const { days } = await getEpisode(e.id);
            return { episode: e, days };
          }),
        );
      }
      const bytes = await fn({
        workspace, profile, master526ez, issues, symptoms: items,
        episodes: episodeBundles,
        scopeIssueId: reportScope, isPro: !isFree,
      });
      const safeTitle = (workspace?.title || "VA-Claim").replace(/[^a-zA-Z0-9-_]+/g, "-");
      const prefix = kind === "evidence" ? "Symptom-Evidence-Summary" : "CP-Exam-Prep";
      saveBytesAsPdf(bytes, `${prefix}-${safeTitle}${isFree ? "-FREE" : ""}.pdf`);
      setReportStatus(kind === "evidence" ? "Evidence Summary generated." : "C&P Prep Sheet generated.");
    } catch (e) {
      setReportStatus(`Error: ${e.message}`);
    } finally {
      setReportBusy(false);
    }
  }

  async function handlePromoteToEpisode(item) {
    if (!window.confirm(
      `Convert this ${fmtDate(item.date)} flare-up into an Ongoing Episode? `
      + `Today's entry becomes Day 1 — you can then add a quick update each day it continues.`,
    )) return;
    try {
      await promoteSymptomToEpisode(item.id);
      window.dispatchEvent(new CustomEvent("claimops:episodes-refresh"));
      await load();
      showToast({
        variant: "success",
        message: "Promoted to Ongoing Episode. Add a daily update from the Episodes panel.",
        duration: 4000,
      });
    } catch (e) {
      showToast({
        variant: "error",
        message: e.response?.data?.detail || `Promote failed: ${e.message}`,
        duration: 5000,
      });
    }
  }

  function openNewEntry(prefill = null, mode = "quick") {
    setEditing(null);
    setFormInitial(prefill);
    setFormMode(mode);
    setShowForm(true);
  }

  function handleDuplicateLast() {
    const latest = items.slice().sort((a, b) => new Date(b.date) - new Date(a.date))[0];
    if (!latest) return;
    openNewEntry(buildDuplicatePayload(latest, toLocalDatetimeInputValue()), "detailed");
  }

  const issuesById = useMemo(() => {
    const m = {};
    issues.forEach((i) => { m[i.id] = i; });
    return m;
  }, [issues]);

  const sortedIssuesForFilter = useMemo(
    () => [...issues].sort((a, b) => (a.claimedAs || "").localeCompare(b.claimedAs || "")),
    [issues],
  );

  return (
    <div className="view-grid" data-testid="symptoms-view">
      <div className="panel master-pdf-panel" style={{ color: "#FFFFFF" }}>
        <div className="master-pdf-inner">
          <div className="master-pdf-left">
            <div className="master-pdf-tag" style={{ color: "#93C5FD" }}>
              <Sparkle size={12} weight="fill" /> CHRONIC-CONDITION DIARY
            </div>
            <h2 className="master-pdf-title" style={{ color: "#FFFFFF" }}>Track symptoms. Spot patterns. Build evidence.</h2>
            <p className="master-pdf-desc" style={{ color: "rgba(255,255,255,0.85)" }}>
              Quick 15-second check-ins, guided flare-up logs, or full evidence entries — your choice.
              Condition-specific trackers adapt to mental health, musculoskeletal, gout, and tinnitus claims.
              Speak your entry and we'll structure it for you.
            </p>
            <ul className="master-pdf-list" style={{ color: "rgba(255,255,255,0.88)" }}>
              <li>Quick Log / Flare-Up / Detailed entry modes</li>
              <li>Insights: trend, flare frequency, medication effectiveness, sleep &amp; stress correlation</li>
              <li>Symptom Evidence Summary + C&amp;P Exam Prep exports</li>
              {isFree && <li style={{ color: "#FCD34D" }}><Lock size={10} weight="bold" /> Free: last {FREE_LIMIT} entries, no photos / body-map</li>}
            </ul>
          </div>
          <div className="master-pdf-right">
            <button
              className="btn btn--accent master-pdf-btn"
              onClick={() => openNewEntry(null, "quick")}
              data-testid="add-symptom-btn"
            >
              <Plus size={16} weight="bold" /> QUICK LOG
            </button>
            <button
              className="btn btn--outline"
              onClick={() => openNewEntry(null, "flare")}
              data-testid="log-flare-now-btn"
              style={{ marginTop: 8 }}
            >
              <Fire size={16} weight="bold" /> LOG FLARE-UP NOW
            </button>
            <button
              className="btn btn--outline"
              onClick={() => openNewEntry(null, "episode")}
              data-testid="start-ongoing-episode-hero-btn"
              style={{ marginTop: 8 }}
            >
              <Calendar size={16} weight="bold" /> START ONGOING EPISODE
            </button>
            <button
              className="btn btn--outline"
              onClick={handleGeneratePdf}
              disabled={generating || !items.length}
              data-testid="generate-symptom-pdf-btn"
              style={{ marginTop: 8 }}
            >
              <FilePdf size={16} weight="bold" /> {generating ? "GENERATING..." : "PRINT / EXPORT PDF"}
            </button>
            {pdfStatus && <div className="master-pdf-status" data-testid="symptom-pdf-status">{pdfStatus}</div>}
            {isFree && setActiveView && (
              <button
                className="btn btn--outline master-pdf-upgrade"
                onClick={() => setActiveView(VIEWS.billing)}
                data-testid="symptom-upgrade-btn"
              >
                Upgrade to Pro for unlimited entries + body map
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="sym-cred-banner" data-testid="credibility-banner">
        <ShieldCheck size={16} weight="bold" />
        <span>
          <strong>Track symptoms accurately. Do not exaggerate.</strong> Consistent, specific logs are more
          useful than dramatic wording. This is a private symptom diary and VA claim organization tool —
          not medical advice and not a guaranteed rating outcome.
        </span>
      </div>

      <EpisodesPanel
        issues={issues}
        onStart={() => openNewEntry(null, "episode")}
        onListChanged={load}
      />

      <div className="panel" data-testid="symptom-stats-panel">
        <div className="panel-header">
          <ChartLine size={16} weight="bold" /> <span>SYMPTOM SUMMARY</span>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 12, padding: 16 }}>
          <Stat label="ENTRIES" value={aggregates.count} testId="stat-count" />
          <Stat label="AVG SEVERITY" value={aggregates.count ? aggregates.avg.toFixed(1) : "—"} color={severityColor(aggregates.avg)} testId="stat-avg" />
          <Stat label="PEAK" value={aggregates.count ? `${aggregates.peak}/10` : "—"} color={severityColor(aggregates.peak)} testId="stat-peak" />
          <Stat label="LAST ENTRY" value={aggregates.last ? fmtDate(aggregates.last).split(",")[0] : "—"} testId="stat-last" />
        </div>
        {isFree && items.length >= FREE_LIMIT && (
          <div style={{ padding: 12, background: "#FEF3C7", border: "1px solid #FCD34D", borderRadius: 4, margin: "0 16px 16px", fontSize: 12, color: "#7C4A03" }} data-testid="symptom-limit-banner">
            <Lock size={12} weight="bold" /> You've hit the free-plan limit of {FREE_LIMIT} entries. Delete an old one or upgrade for unlimited history.
          </div>
        )}
      </div>

      {items.length > 0 && (
        <div className="panel" data-testid="symptom-charts-panel">
          <div className="panel-header">
            <ChartLine size={16} weight="bold" /> <span>TREND ANALYSIS</span>
            <span className="header-count mono">last 30 days</span>
          </div>
          <div className="panel-body">
            <SymptomCharts items={items} days={30} />
          </div>
        </div>
      )}

      {items.length > 0 && (
        <div className="panel" data-testid="symptom-insights-panel">
          <div className="panel-header">
            <TrendUp size={16} weight="bold" /> <span>VA EVIDENCE INSIGHTS</span>
          </div>
          <div className="panel-body">
            <SymptomInsightsPanel items={items} />
          </div>
        </div>
      )}

      <div className="panel" data-testid="symptom-reports-panel">
        <div className="panel-header">
          <FileText size={16} weight="bold" /> <span>VA EVIDENCE REPORTS</span>
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center", padding: "14px 16px 6px" }}>
          <select
            className="field-input symptom-filter-select"
            value={reportScope}
            onChange={(e) => setReportScope(e.target.value)}
            data-testid="report-scope-select"
          >
            <option value="">All conditions</option>
            {sortedIssuesForFilter.map((i) => <option key={i.id} value={i.id}>{i.claimedAs}</option>)}
          </select>
          <button
            className="btn btn--accent"
            disabled={reportBusy || !items.length}
            onClick={() => runReport("evidence")}
            data-testid="evidence-summary-pdf-btn"
          >
            <FilePdf size={14} weight="bold" /> EVIDENCE SUMMARY
          </button>
          <button
            className="btn btn--outline"
            disabled={reportBusy || !items.length}
            onClick={() => runReport("cpprep")}
            data-testid="cp-prep-pdf-btn"
          >
            <FileText size={14} weight="bold" /> C&amp;P EXAM PREP SHEET
          </button>
          {reportStatus && <span data-testid="report-pdf-status" style={{ fontSize: 12, color: reportStatus.startsWith("Error") ? "#C72E2E" : "#1A8C52", fontWeight: 600 }}>{reportStatus}</span>}
        </div>
        <div style={{ padding: "8px 16px 14px", fontSize: 12, color: "#64748B", lineHeight: 1.5 }}>
          <strong>Evidence Summary:</strong> per-condition rollup of frequency, severity, duration, triggers,
          treatment, and functional impact. <strong>C&amp;P Prep Sheet:</strong> a one-page memory aid for your
          exam — facts pulled from your own log, not a script.
        </div>
      </div>

      <div className="panel" data-testid="symptom-list-panel">
        <div className="panel-header">
          <Pulse size={16} weight="bold" /> <span>SYMPTOM LOG</span>
          <div style={{ marginLeft: "auto", display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <button
              className="btn btn--outline"
              style={{ padding: "5px 10px", fontSize: 10.5 }}
              onClick={handleDuplicateLast}
              disabled={!items.length}
              data-testid="duplicate-last-btn"
              title="Start a new entry pre-filled from your most recent log"
            >
              <CopySimple size={12} weight="bold" /> DUPLICATE LAST
            </button>
            <select
              className="field-input symptom-filter-select"
              value={filterIssue}
              onChange={(e) => setFilterIssue(e.target.value)}
              data-testid="symptom-filter-issue"
            >
              <option value="">All conditions</option>
              <option value="__undx__">Undiagnosed only</option>
              {sortedIssuesForFilter.map((i) => <option key={i.id} value={i.id}>{i.claimedAs}</option>)}
            </select>
            <select
              className="field-input symptom-filter-select"
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              data-testid="symptom-filter-type"
            >
              <option value="all">All types</option>
              <option value="daily">Daily</option>
              <option value="flare">Flares</option>
            </select>
            <select
              className="field-input symptom-filter-select"
              value={filterTag}
              onChange={(e) => setFilterTag(e.target.value)}
              data-testid="symptom-filter-tag"
            >
              <option value="">All tags</option>
              {allTags.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
        </div>
        {loading ? (
          <div style={{ padding: 24, color: "#64748B" }}>Loading...</div>
        ) : filtered.length === 0 ? (
          <div className="empty-state" data-testid="symptom-empty">
            <Pulse size={28} weight="duotone" />
            <div className="empty-state-title">No symptom entries yet</div>
            <div className="empty-state-desc">
              Track flares, daily severity, and functional impact. A 90-day log carries serious weight at a C&amp;P exam and exports to a clean PDF.
            </div>
            <button
              className="btn btn--accent"
              onClick={() => openNewEntry(null, "quick")}
              data-testid="symptom-empty-cta"
            >
              <Plus size={12} weight="bold" /> LOG YOUR FIRST ENTRY
            </button>
          </div>
        ) : (
          <div className="table-scroll">
            <div className="table-scroll-hint">← scroll →</div>
          <table className="table table--cards-on-mobile" data-testid="symptom-table">
            <thead>
              <tr>
                <th>DATE</th>
                <th>TYPE</th>
                <th>SYMPTOM</th>
                <th>CONDITION</th>
                <th>SEV</th>
                <th>IMPACT</th>
                <th style={{ width: 88 }}></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((s) => (
                <tr key={s.id} data-testid={`symptom-row-${s.id}`}>
                  <td data-label="DATE" style={{ whiteSpace: "nowrap" }}>{fmtDate(s.date)}</td>
                  <td data-label="TYPE">
                    <span className={`rem-status ${s.entryType === "flare" ? "rem-status--overdue" : s.entryType === "episode" ? "rem-status--info" : "rem-status--ok"}`} style={{ fontSize: 10 }}>
                      {s.entryType === "flare" ? "FLARE" : s.entryType === "episode" ? `EP D${s.episodeDayNumber || ""}` : "DAILY"}
                    </span>
                    {s.entryType === "flare" && !s.episodeId && (
                      <button
                        type="button"
                        className="appt-link-chip"
                        onClick={() => handlePromoteToEpisode(s)}
                        data-testid={`symptom-promote-inline-${s.id}`}
                        title="This flare is still going — convert into an Ongoing Episode"
                        style={{ marginLeft: 6, padding: "2px 8px", fontSize: 9.5, fontWeight: 700, letterSpacing: "0.04em" }}
                      >
                        <ArrowsClockwise size={10} weight="bold" /> STILL GOING
                      </button>
                    )}
                  </td>
                  <td data-label="SYMPTOM">
                    <strong>{s.symptom}</strong>
                    {(s.tags || []).length > 0 && (
                      <div data-testid={`symptom-tags-${s.id}`}>
                        {(s.tags || []).slice(0, 3).map((t) => <span key={t} className="sym-tagchip">{t}</span>)}
                      </div>
                    )}
                  </td>
                  <td data-label="CONDITION" style={{ fontSize: 12, color: "#475569" }}>
                    {s.linkedIssueId ? (
                      issuesById[s.linkedIssueId]?.claimedAs || "Linked"
                    ) : s.isUndiagnosed ? (
                      <span style={{ color: "#7C4A03", fontWeight: 700 }} data-testid={`symptom-undx-${s.id}`}>
                        UNDIAGNOSED{s.suspectedCondition ? ` · ${s.suspectedCondition}` : ""}
                      </span>
                    ) : "General"}
                  </td>
                  <td data-label="SEV">
                    <span style={{ fontWeight: 800, color: severityColor(s.severity), fontFamily: "JetBrains Mono, monospace" }}>
                      {s.severity}/10
                    </span>
                  </td>
                  <td data-label="IMPACT" style={{ fontSize: 11, color: "#64748B" }}>{(s.impact || []).slice(0, 2).join(", ")}</td>
                  <td style={{ textAlign: "right" }}>
                    <button className="icon-btn" onClick={() => { setFormInitial(null); setEditing(s); setShowForm(true); }} data-testid={`symptom-edit-${s.id}`} title="Edit">
                      <PencilSimple size={14} weight="bold" />
                    </button>
                    <button className="icon-btn icon-btn--danger" onClick={() => handleDelete(s)} data-testid={`symptom-delete-${s.id}`} title="Delete">
                      <Trash size={14} weight="bold" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>

      {showForm && (
        <SymptomEntryForm
          initial={editing || formInitial}
          initialMode={formMode}
          issues={issues}
          isFree={isFree}
          prescriptions={prescriptions}
          onCancel={() => { setShowForm(false); setEditing(null); setFormInitial(null); }}
          onSave={handleSave}
        />
      )}

      <button
        type="button"
        className="mobile-fab"
        onClick={() => openNewEntry(null, "quick")}
        aria-label="Log symptom entry"
        data-testid="symptom-mobile-fab"
      >
        <Plus size={20} weight="bold" />
      </button>
    </div>
  );
}

function Stat({ label, value, color, testId }) {
  return (
    <div style={{ background: "#F8FAFC", border: "1px solid #E2E8F0", borderRadius: 4, padding: 14 }} data-testid={testId}>
      <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.08em", color: "#64748B" }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 800, color: color || "#0F172A", marginTop: 4, fontFamily: "JetBrains Mono, monospace" }}>
        {value}
      </div>
    </div>
  );
}

// end
